// === Admin: ML Hours ===
// Full CRUD page — list, add, edit, soft-delete.

import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Pagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { alpha, darken } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { AppTableHead, AppTableHeadCell, AppTableRow, DEFAULT_ROWS_PER_PAGE, ROWS_PER_PAGE_OPTIONS } from '../components/AppTable';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MlHourRow {
  readonly id: number;
  readonly rate: number;
  readonly active: boolean;
  readonly created_at: string | null;
  readonly updated_at: string | null;
}

interface FormState {
  rate: string;
  active: boolean;
  created_at: string | null;
  updated_at: string | null;
}

const DEFAULT_FORM: FormState = { rate: '', active: true, created_at: null, updated_at: null };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getApiUrl(): string {
  return (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:8787';
}

function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function fmtAuDate(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-AU', { dateStyle: 'short', timeStyle: 'medium', hour12: false }).format(new Date(iso));
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export function MlHoursPage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const token = session?.access_token ?? '';

  // ---- List state ----
  const [rows, setRows] = useState<readonly MlHourRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); setDebouncedSearch(search); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // ---- Add / Edit dialog ----
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // ---- Delete dialog ----
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteLabel, setDeleteLabel] = useState('');
  const [deleting, setDeleting] = useState(false);

  // ---- Snackbar ----
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const showSnack = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnack({ open: true, message, severity });

  // ---------------------------------------------------------------------------
  // Fetch list
  // ---------------------------------------------------------------------------
  const fetchList = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setListError(null);
    try {
      const url = new URL(`${getApiUrl()}/api/v1/admin/ml-hours`);
      url.searchParams.set('page', String(page + 1));
      url.searchParams.set('limit', String(rowsPerPage));
      if (debouncedSearch) url.searchParams.set('search', debouncedSearch);

      const res = await fetch(url.toString(), { headers: authHeaders(token) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { data: MlHourRow[]; total: number };
      setRows(json.data);
      setTotal(json.total);
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Failed to load ml hours');
    } finally {
      setLoading(false);
    }
  }, [token, page, rowsPerPage, debouncedSearch]);

  useEffect(() => { void fetchList(); }, [fetchList]);

  // ---------------------------------------------------------------------------
  // Open Add / Edit
  // ---------------------------------------------------------------------------
  const openAdd = () => {
    setEditingId(null);
    setForm(DEFAULT_FORM);
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = async (id: number) => {
    if (!token) return;
    setFormError(null);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/ml-hours/${id}`, {
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error('Failed to load record');
      const json = (await res.json()) as { data: MlHourRow };
      setForm({ rate: String(json.data.rate), active: json.data.active, created_at: json.data.created_at ?? null, updated_at: json.data.updated_at ?? null });
      setEditingId(id);
      setDialogOpen(true);
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Failed to load record', 'error');
    }
  };

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------
  const handleSave = async () => {
    const rateNum = parseInt(form.rate, 10);
    if (!form.rate.trim() || isNaN(rateNum) || rateNum <= 0) {
      setFormError('Rate must be a positive whole number.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const url = editingId
        ? `${getApiUrl()}/api/v1/admin/ml-hours/${editingId}`
        : `${getApiUrl()}/api/v1/admin/ml-hours`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({ rate: rateNum, active: form.active }),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      setDialogOpen(false);
      showSnack(editingId ? 'Rate updated.' : 'Rate added.');
      void fetchList();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Delete
  // ---------------------------------------------------------------------------
  const confirmDelete = (id: number, label: string) => { setDeleteId(id); setDeleteLabel(label); };

  const handleDelete = async () => {
    if (!deleteId || !token) return;
    setDeleting(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/ml-hours/${deleteId}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDeleteId(null);
      showSnack('Rate deleted.');
      void fetchList();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Delete failed', 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const totalPages = Math.ceil(total / rowsPerPage);

  return (
    <>
      <Container maxWidth="xl" sx={{ py: 4 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <Tooltip title="Back to Dashboard">
            <IconButton onClick={() => navigate('/dashboard')} size="small">
              <BackIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>ML Hours</Typography>
            <Typography variant="body2" color="text.secondary">
              Millilitres per hour rate reference data
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add Rate
          </Button>
        </Box>

        {/* Toolbar */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search by rate…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 260 }}
            />
            {search && (
              <Button size="small" onClick={() => { setSearch(''); setPage(0); }}>Clear</Button>
            )}
            <Box sx={{ ml: 'auto' }}>
              <Chip label={`${total} records`} size="small" variant="outlined" />
            </Box>
          </Box>
        </Paper>

        {/* Error */}
        {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}

        {/* Table */}
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer>
            <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
              <AppTableHead>
                <TableRow>
                  <AppTableHeadCell>Rate (ml/hr)</AppTableHeadCell>
                  <AppTableHeadCell sx={{ width: 120 }}>Active</AppTableHeadCell>
                  <AppTableHeadCell align="right" sx={{ width: 96 }}>Actions</AppTableHeadCell>
                </TableRow>
              </AppTableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 6 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                      No records found
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <AppTableRow key={row.id}>
                      <TableCell sx={{ fontWeight: 600 }}>{row.rate}</TableCell>
                      <TableCell>
                        <Chip
                          label={row.active ? 'Active' : 'Inactive'}
                          size="small"
                          color={row.active ? 'success' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => void openEdit(row.id)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => confirmDelete(row.id, String(row.rate))}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </AppTableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination bar */}
          <Box sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: theme.palette.mode === 'light'
              ? alpha(theme.palette.primary.main, 0.09)
              : darken(theme.palette.background.paper, 0.35),
            px: 2,
            py: 1.5,
          })}>
            <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85 })}>
              {loading ? 'Loading…' : total === 0 ? 'No records found' :
                `Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, total)} of ${total} entries`
              }
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}>Show</Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
                    '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
                    '& .MuiSelect-select': { py: 0.5, px: 1 },
                  })}
                >
                  {ROWS_PER_PAGE_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>{n}</MenuItem>
                  ))}
                </Select>
              </Box>

              {total > 0 && (
                <Typography variant="body2" sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}>
                  Page {page + 1} of {totalPages}
                </Typography>
              )}

              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={page + 1}
                  onChange={(_, p) => setPage(p - 1)}
                  color="primary"
                  size="small"
                  disabled={loading}
                  sx={(theme) => ({
                    '& .MuiPaginationItem-root': {
                      color: theme.palette.text.primary,
                      borderColor: theme.palette.divider,
                      '&:hover': { backgroundColor: theme.palette.action.hover },
                    },
                    '& .Mui-selected': {
                      backgroundColor: `${theme.palette.primary.main} !important`,
                      color: theme.palette.primary.contrastText,
                    },
                    '& .Mui-disabled': { opacity: 0.5 },
                  })}
                />
              )}
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* ------------------------------------------------------------------ */}
      {/* Add / Edit Dialog                                                    */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Rate' : 'Add Rate'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          {formError && <Alert severity="error">{formError}</Alert>}
          <TextField
            label="Rate (ml/hr)"
            size="small"
            fullWidth
            required
            type="number"
            placeholder="e.g. 250"
            inputProps={{ min: 1, step: 1 }}
            value={form.rate}
            onChange={(e) => setForm((f) => ({ ...f, rate: e.target.value }))}
            helperText="Whole number — millilitres per hour"
          />
          <Select
            size="small"
            value={form.active ? 'true' : 'false'}
            onChange={(e) => setForm((f) => ({ ...f, active: e.target.value === 'true' }))}
            fullWidth
          >
            <MenuItem value="true">Active</MenuItem>
            <MenuItem value="false">Inactive</MenuItem>
          </Select>

          {/* Audit Trail */}
          {editingId && (
            <Box
              mt={1}
              p={2}
              sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.12), borderRadius: 1 }}
            >
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1} sx={{ textTransform: 'uppercase', letterSpacing: 0.8, textAlign: 'center' }}>
                Audit Trail
              </Typography>
              <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.disabled" display="block">Created</Typography>
                  <Typography variant="caption" color="text.secondary">{fmtAuDate(form.created_at)}</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.disabled" display="block">Updated</Typography>
                  <Typography variant="caption" color="text.secondary">{fmtAuDate(form.updated_at)}</Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} /> : undefined}
          >
            {editingId ? 'Save Changes' : 'Add Rate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Delete Confirmation                                                  */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)} maxWidth="xs">
        <DialogTitle>Delete Rate</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete rate <strong>{deleteLabel} ml/hr</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleDelete()}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} /> : undefined}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Snackbar                                                             */}
      {/* ------------------------------------------------------------------ */}
      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </>
  );
}
